import { Form, Checkbox, Input, InputNumber, Radio, Select, Slider, Button, AutoComplete, Tooltip } from "antd";
import { Field, useField } from "formik";
import LocationSelect from './LocationSelect';
import { MinusCircleOutlined, PlusOutlined, HistoryOutlined } from "@ant-design/icons";
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';
import React, { useState, useEffect } from 'react';

const createField = (Component) => ({ name, label, placeholder, ...props }) => (
  <Field name={name}>
    {({ field, form, meta }) => {
      const handleChange = (value) => {
        // Handle Checkbox separately
        if (Component === Checkbox) {
          form.setFieldValue(name, value.target.checked);
          return;
        }
        // Handle Select components
        if (Component === Select || Component === Slider || Component === InputNumber) {
          form.setFieldValue(name, value);
          return;
        }
        // Handle other components
        form.setFieldValue(name, value?.target?.value ?? value);
      };

      const handleBlur = () => {
        form.setFieldTouched(name, true);
      };

      const commonProps = {
        ...props,
        placeholder: placeholder || label,
        onChange: handleChange,
        onBlur: handleBlur,
      };

      // Special handling for different components
      if (Component === Checkbox) {
        commonProps.checked = field.value;
      } else if (Component === Select) {
        commonProps.value = field.value;
      } else if (Component === Slider || Component === InputNumber) {
        commonProps.value = field.value;
      } else {
        commonProps.value = field.value;
      }

      return (
        <Form.Item
          label={label}
          validateStatus={meta.touched && meta.error ? 'error' : ''}
          help={meta.touched && meta.error}
          style={{ marginBottom: '16px' }}
        >
          <Component {...commonProps} style={{ width: '100%' }} />
        </Form.Item>
      );
    }}
  </Field>
);

export const TextField = createField(Input);

// Special version of TextField for postal code with helper text
export const PostalCodeField = ({ name, label, placeholder, ...props }) => {
  const [field, meta, helpers] = useField(name);
  
  const handleChange = (e) => {
    helpers.setValue(e.target.value);
  };
  
  const handleBlur = () => {
    helpers.setTouched(true);
  };
  
  return (
    <Form.Item
      label={label}
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={
        <div>
          {meta.touched && meta.error ? (
            <div style={{ color: '#ff4d4f' }}>{meta.error}</div>
          ) : null}
          <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
            Note: When using ZIP/Postal code search, it takes precedence over the Location field
          </div>
        </div>
      }
    >
      <Input
        {...props}
        value={field.value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder || label}
        style={{ width: '100%' }}
      />
    </Form.Item>
  );
};

// Special version of TextField for keyword searches
export const KeywordField = ({ name, label, placeholder, customOnChange, ...props }) => {
  const [field, meta, helpers] = useField(name);
  const { keywords } = useSearchSuggestions();
  const [options, setOptions] = useState([]);
  const [focused, setFocused] = useState(false);
  
  // Update options when keywords change or when user types
  useEffect(() => {
    if (!keywords || !keywords.length) return;
    
    // If user has typed something, filter suggestions
    if (field.value) {
      const filteredOptions = keywords
        .filter(keyword => 
          keyword.toLowerCase().includes(field.value.toLowerCase()))
        .map(keyword => ({
          value: keyword,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{keyword}</span>
              <Tooltip title="From your search history">
                <HistoryOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </div>
          )
        }));
      setOptions(filteredOptions);
    } else if (focused) {
      // If input is empty but focused, show recent searches
      const recentOptions = keywords.slice(0, 5).map(keyword => ({
        value: keyword,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{keyword}</span>
            <Tooltip title="From your search history">
              <HistoryOutlined style={{ color: '#1890ff' }} />
            </Tooltip>
          </div>
        )
      }));
      setOptions(recentOptions);
    } else {
      setOptions([]);
    }
  }, [keywords, field.value, focused]);
  
  const handleChange = (value) => {
    helpers.setValue(value);
    
    // Call the custom onChange handler if provided
    if (customOnChange) {
      customOnChange(value);
    }
  };
  
  const handleBlur = () => {
    helpers.setTouched(true);
    setFocused(false);
  };
  
  const handleFocus = () => {
    setFocused(true);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Find the closest form and submit it
      const form = e.target.closest('form');
      if (form) {
        e.preventDefault();
        const submitEvent = new Event('submit', { cancelable: true, bubbles: true });
        form.dispatchEvent(submitEvent);
      }
    }
  };
  
  return (
    <Form.Item
      label={label}
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={meta.touched && meta.error}
    >
      <AutoComplete
        {...props}
        options={options}
        value={field.value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyPress={handleKeyPress}
        placeholder={placeholder || label}
        allowClear
        style={{ width: '100%' }}
        filterOption={false}
        notFoundContent={focused && !field.value ? "No recent searches" : "No matching suggestions"}
      />
    </Form.Item>
  );
};

// Special version of TextField for job title searches
export const JobTitleField = ({ name, label, placeholder, ...props }) => {
  const [field, meta, helpers] = useField(name);
  const { jobTitles } = useSearchSuggestions();
  const [options, setOptions] = useState([]);
  const [focused, setFocused] = useState(false);
  
  // Update options when job titles change or when user types
  useEffect(() => {
    if (!jobTitles || !jobTitles.length) return;
    
    // If user has typed something, filter suggestions
    if (field.value) {
      const filteredOptions = jobTitles
        .filter(title => 
          title.toLowerCase().includes(field.value.toLowerCase()))
        .map(title => ({
          value: title,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{title}</span>
              <Tooltip title="From your search history">
                <HistoryOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </div>
          )
        }));
      setOptions(filteredOptions);
    } else if (focused) {
      // If input is empty but focused, show recent searches
      const recentOptions = jobTitles.slice(0, 5).map(title => ({
        value: title,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{title}</span>
            <Tooltip title="From your search history">
              <HistoryOutlined style={{ color: '#1890ff' }} />
            </Tooltip>
          </div>
        )
      }));
      setOptions(recentOptions);
    } else {
      setOptions([]);
    }
  }, [jobTitles, field.value, focused]);
  
  const handleChange = (value) => {
    helpers.setValue(value);
  };
  
  const handleBlur = () => {
    helpers.setTouched(true);
    setFocused(false);
  };
  
  const handleFocus = () => {
    setFocused(true);
  };
  
  return (
    <Form.Item
      label={label}
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={meta.touched && meta.error}
    >
      <AutoComplete
        {...props}
        options={options}
        value={field.value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder || label}
        allowClear
        style={{ width: '100%' }}
        filterOption={false}
        notFoundContent={focused && !field.value ? "No recent searches" : "No matching suggestions"}
      />
    </Form.Item>
  );
};

export const SelectField = createField(({ options, ...props }) => (
  <Select {...props}>
    {options?.map(option => (
      <Select.Option key={option.value} value={option.value}>
        {option.label}
      </Select.Option>
    ))}
  </Select>
));

export const CheckboxField = createField(Checkbox);

export const RangeField = createField(Slider);

export const MultiSelectField = createField(({ options, ...props }) => (
  <Select mode="multiple" {...props}>
    {options?.map(option => (
      <Select.Option key={option.value} value={option.value}>
        {option.label}
      </Select.Option>
    ))}
  </Select>
));

export const NumberField = createField(InputNumber);

export const RadioField = createField(({ options, ...props }) => (
  <Radio.Group {...props}>
    {options?.map(option => (
      <Radio key={option.value} value={option.value}>
        {option.label}
      </Radio>
    ))}
  </Radio.Group>
));

export const LocationField = createField(LocationSelect);

export const CustomRangeField = ({ ...props }) => (
  <Input.Group compact style={{ display: 'flex', gap: '8px' }}>
    <InputNumber
      style={{ width: '100%' }}
      placeholder="Min"
      {...props}
    />
    <InputNumber
      style={{ width: '100%' }}
      placeholder="Max"
      {...props}
    />
  </Input.Group>
);

export const SalaryRangeField = ({ name, currencies, types, includeNormalized, includeSalaryNotSpecified, ...props }) => {
  const [field, meta, helpers] = useField(name);

  const handleChange = (type, value) => {
    helpers.setValue({
      ...field.value,
      [type]: value
    });
  };

  return (
    <Form.Item
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={meta.touched && meta.error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Input.Group compact style={{ display: 'flex', gap: '8px' }}>
          <Select
            style={{ width: '25%' }}
            placeholder="Currency"
            options={currencies}
            value={field.value?.currency}
            onChange={(value) => handleChange('currency', value)}
          />
          <Select
            style={{ width: '25%' }}
            placeholder="Type"
            options={types}
            value={field.value?.type}
            onChange={(value) => handleChange('type', value)}
          />
          <InputNumber
            style={{ width: '25%' }}
            placeholder="Min"
            value={field.value?.min}
            onChange={(value) => handleChange('min', value)}
          />
          <InputNumber
            style={{ width: '25%' }}
            placeholder="Max"
            value={field.value?.max}
            onChange={(value) => handleChange('max', value)}
          />
        </Input.Group>
        {includeNormalized && (
          <Input.Group compact style={{ display: 'flex', gap: '8px' }}>
            <div style={{ width: '50%', paddingLeft: '8px' }}>Normalized (Annual)</div>
            <InputNumber
              style={{ width: '25%' }}
              placeholder="Min"
              value={field.value?.normalizedMin}
              onChange={(value) => handleChange('normalizedMin', value)}
            />
            <InputNumber
              style={{ width: '25%' }}
              placeholder="Max"
              value={field.value?.normalizedMax}
              onChange={(value) => handleChange('normalizedMax', value)}
            />
          </Input.Group>
        )}
        {includeSalaryNotSpecified && (
          <Checkbox
            checked={field.value?.includeSalaryNotSpecified}
            onChange={(e) => handleChange('includeSalaryNotSpecified', e.target.checked)}
          >
            Include resumes without salary specified
          </Checkbox>
        )}
      </div>
    </Form.Item>
  );
};

export const LanguageSkillField = ({ name, proficiencyLevels, ...props }) => {
  const [field, meta, helpers] = useField(name);

  const handleAddLanguage = () => {
    const currentLanguages = field.value || [];
    helpers.setValue([...currentLanguages, { language: '', proficiency: '' }]);
  };

  const handleRemoveLanguage = (index) => {
    const newLanguages = [...field.value];
    newLanguages.splice(index, 1);
    helpers.setValue(newLanguages);
  };

  const handleLanguageChange = (index, type, value) => {
    const newLanguages = [...field.value];
    newLanguages[index] = {
      ...newLanguages[index],
      [type]: value
    };
    helpers.setValue(newLanguages);
  };

  return (
    <Form.Item
      validateStatus={meta.touched && meta.error ? 'error' : ''}
      help={meta.touched && meta.error}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {(field.value || []).map((language, index) => (
          <Input.Group key={index} compact style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Select
              style={{ width: '50%' }}
              placeholder="Select Language"
              value={language.language}
              onChange={(value) => handleLanguageChange(index, 'language', value)}
              options={[
                { value: "17", label: "Chinese" },
                { value: "32", label: "French" },
                { value: "39", label: "German" },
                { value: "42", label: "Hindi" },
                { value: "49", label: "Japanese" },
                { value: "82", label: "Spanish" },
              ]}
            />
            <Select
              style={{ width: '40%' }}
              placeholder="Proficiency"
              value={language.proficiency}
              onChange={(value) => handleLanguageChange(index, 'proficiency', value)}
              options={proficiencyLevels}
            />
            <Button
              type="text"
              icon={<MinusCircleOutlined />}
              onClick={() => handleRemoveLanguage(index)}
            />
          </Input.Group>
        ))}
        <Button type="dashed" onClick={handleAddLanguage} block icon={<PlusOutlined />}>
          Add Language
        </Button>
      </div>
    </Form.Item>
  );
};


